import React, { Component } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { banner } from 'actions';
import { Button, Input, Select } from 'common/components';
import { graphQlToFormErrors, capitalizeString } from 'common/utils';
import {
  required,
  numberOnly,
  maxLength,
  maxValue,
  minValue,
  noPercent,
  notOneOf,
} from 'common/form-validators';
import { limitDigits, onlyNumbers, formatGPS, trim } from 'common/normalizers';
import { toCommaNumber } from 'common/formatters';
import { edit as Edit } from 'common/svg/icons';

import {
  MUTATION_CREATE_STORAGE,
  MUTATION_UPDATE_STORAGE,
  MUTATION_RENAME_STORAGE,
} from 'api/storage';
import {
  ANALYTICS,
  BANNER_IDS,
  STORAGE_SIZE,
  ERROR_DEFAULT,
  OPTIONS,
  CROP_SIZE,
} from 'common/constants';
import mixpanel from 'mixpanel-browser';

export class StorageForm extends Component {
  static displayName = 'StorageForm';

  constructor(props) {
    super(props);

    // validations
    this.nameMax = value => maxLength(value, STORAGE_SIZE.MAX_TITLE);
    this.notesMax = value => maxLength(value, STORAGE_SIZE.MAX_NOTES);
    this.varietyMax = value => maxLength(value, CROP_SIZE.MAX_TITLE);
    this.storageSizeMin = value => minValue(value, STORAGE_SIZE.MIN);
    this.storageSizeMax = value =>
      maxValue(value, STORAGE_SIZE.MAX)
        ? `Bin Capacity should be between 0 and ${toCommaNumber(
            STORAGE_SIZE.MAX
          )} Bushels`
        : undefined;

    // normlizers
    this.normalizeFarmSize = value => limitDigits(onlyNumbers(value), 7, 2);
    this.normalizedGPS = value => value && formatGPS(value);
  }

  static propTypes = {
    // manually added
    closeForm: PropTypes.func.isRequired,
    id: PropTypes.string,
    location: PropTypes.object,
    storage: PropTypes.object,
    existingStorageNames: PropTypes.arrayOf(PropTypes.string),
    binYardOptions: PropTypes.arrayOf(
      PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
    ),
    toggleFieldView: PropTypes.func,

    // redux-responsive
    browser: PropTypes.object,

    // reduxform
    error: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    valid: PropTypes.bool.isRequired,
    pristine: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,

    // from graphql
    initialValues: PropTypes.object,
    used: PropTypes.number,
    // from dispatch
    onSubmit: PropTypes.func.isRequired,
  };

  static defaultProps = {
    closeForm: () => null,
    binYardOptions: [],
  };

  // cannot be in constructor, because initialValues change after graphQL query
  capacityMin = value => {
    // if this is edit mode, do max + initial value
    const min = this.props.used || STORAGE_SIZE.MIN;
    return (
      minValue(value, min) &&
      `Storage already contains ${min} Bushels. Use a higher number or move crop`
    );
  };

  cropMax = value => {
    const max =
      (this.props.initialValues && this.props.initialValues.capacity) ||
      STORAGE_SIZE.max;
    return (
      maxValue(value, max) &&
      'Current quantity should be less than the total capacity'
    );
  };

  storageNameTaken = value => {
    const { storage } = this.props;
    const { existingStorageNames } = this.props;
    if (!existingStorageNames) return true;
    return (
      ((storage && value !== storage.name) || !storage) &&
      notOneOf(value, existingStorageNames)
    );
  };

  conditionalRequired = (value, formState) => {
    if (formState.crop) {
      return value
        ? undefined
        : 'This field is required if you have chosen a crop.';
    } else {
      return !value ? undefined : 'You need to choose a crop to use this field';
    }
  };
  render() {
    const {
      browser,
      onSubmit,
      closeForm,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
      toggleFieldView,
      binYardOptions,
    } = this.props;

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <small className={'text--info'}>* Required</small>
        <h2 className={'text--header'}>Storage Details</h2>

        <Input
          name="name"
          disabled={submitting}
          title="Storage Name"
          validate={[required, noPercent, this.nameMax, this.storageNameTaken]}
          normalizeOnBlur={trim}
        />

        <Input
          name="capacity"
          disabled={submitting}
          title="Total Capacity"
          infoText="Bushels"
          validate={[
            required,
            numberOnly,
            this.storageSizeMin,
            this.storageSizeMax,
            this.capacityMin,
          ]}
          normalize={this.normalizeFarmSize}
          format={toCommaNumber}
        />

        <Input
          name="location"
          disabled={submitting}
          title="GPS Coordinates"
          readOnly={true}
          validate={[required]}
          format={this.normalizedGPS}
          withButton={
            browser.greaterThan.mobile ? null : (
              <Button
                onClick={e => {
                  e.preventDefault();
                  toggleFieldView();
                }}
              >
                <Edit width={24} />
              </Button>
            )
          }
        />
        <small>
          Move the pin on the map to readjust coordinates for storage.
        </small>

        <Select
          name="binYard"
          disabled={submitting}
          title="Bin Yard"
          disablePromptOption
          isCreatable
          options={binYardOptions}
        />

        <Input
          name="notes"
          disabled={submitting}
          title="Notes"
          validate={[this.notesMax]}
          normalizeOnBlur={trim}
        />
        <small>
          Add notes on grain quality, movement / storage instructions, etc.
        </small>

        <h2 className={'text--header'}>
          Crop <small>(Optional)</small>
        </h2>
        <Select
          name="crop"
          disabled={submitting}
          title="Crop"
          disablePromptOption={false}
          options={OPTIONS.CROP_OPTIONS}
        />
        <Input
          name="variety"
          disabled={submitting}
          title="Variety"
          normalize={capitalizeString}
          normalizeOnBlur={trim}
          validate={[this.varietyMax]}
        />
        <Input
          name="used"
          disabled={submitting}
          infoText="Bushels"
          title="Current Quantity"
          validate={[numberOnly, this.cropMax, this.conditionalRequired]}
          normalize={this.normalizeFarmSize}
          format={toCommaNumber}
          theme={['tight']}
        />

        {error && <p className="text--error">{error}</p>}

        <div className={'field__group'}>
          <fieldset className={'field text--center'}>
            <Button
              type="submit"
              className="btn btn--primary gap-top-small"
              disabled={!valid || pristine || submitting}
            >
              Save
            </Button>
          </fieldset>

          <fieldset className={'field text--center'}>
            <Button
              type="button"
              className="btn"
              onClick={closeForm}
              disabled={submitting}
            >
              Cancel
            </Button>
          </fieldset>
        </div>
      </form>
    );
  }
}

const formSettings = {
  form: 'storageform',
  enableReinitialize: false,
  destroyOnUnmount: false,
};
const mapState = ({ browser }, ownProps) => {
  const { storage } = ownProps;
  const props = { browser };
  if (storage) {
    Object.assign(props, {
      browser,
      initialValues: {
        used: (storage.capacity && storage.capacity.used) || 0,
        crop: storage.crop && storage.crop.name,
        variety: storage.crop && storage.crop.variety,
        name: storage.name,
        capacity: storage.capacity && storage.capacity.size,
        location:
          storage.location &&
          `${storage.location.lat}, ${storage.location.lng}`,
        binYard: storage.binYard,
        notes: storage.notes,
      },
    });
  }
  return props;
};

const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    // Location arrives here in one string like: "54.17942677164299, -115.02104926477051"
    // Remove whitespace and split so we can build a {lat, lng} object
    const [lat, lng] =
      values.location && values.location.replace(/\s/g, '').split(',');
    const location = { lat, lng };
    let id = props.id;
    let vars = {
      capacity: {
        used: values.used,
        size: values.capacity,
        type: 'bushels', // BE has default values too
      },
      location,
      binYard: values.binYard ? values.binYard : null,
      notes: values.notes ? values.notes : null,
    };

    if (values.crop) {
      vars.crop = {
        name: values.crop,
        variety: values.variety,
      };
    }

    if (props.id === 'add') {
      vars = {
        name: values.name.trim(),
        ...vars,
      };
    }

    // get new id only when the storage name is updated
    const initialName = props.storage && props.storage.name;
    if (props.id !== 'add' && initialName !== values.name) {
      id = await props.onRenameStorage(props.id, values.name.trim());
    }

    const res =
      props.id === 'add'
        ? await props.onCreateStorage(vars)
        : await props.onUpdateStorage(id, vars);

    if (res && res.data && (res.data.createStorage || res.data.updateStorage)) {
      dispatch(
        banner.display(
          BANNER_IDS.APP,
          `STORAGE ${props.id === 'add' ? 'ADDED' : 'UPDATED'}`,
          false
        )
      );
      props.closeForm();
    } else if (res && res.errors) {
      const _error = res.errors[0] ? res.errors[0].message : ERROR_DEFAULT;
      throw new SubmissionError({ _error });
    } else {
      throw new SubmissionError({ _error: ERROR_DEFAULT });
    }
  },
});

export default compose(
  graphql(MUTATION_CREATE_STORAGE, {
    options: () => ({
      onCompleted: ({ createStorage }) => {
        if (createStorage) {
          mixpanel.track(ANALYTICS.EVENTS.STORAGE.ADDED);
          mixpanel.people.increment(
            ANALYTICS.PROPERTIES.STORAGE.STORAGES_ADDED
          );
        }
      },
    }),
    props: props => ({
      onCreateStorage: storageCreateInput => {
        return props
          .mutate({
            variables: { storageCreateInput },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  graphql(MUTATION_RENAME_STORAGE, {
    props: props => ({
      onRenameStorage: (id, newName) => {
        return props
          .mutate({
            variables: { id, newName },
          })
          .then(({ data }) => {
            return data.renameStorage.id;
          })
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  graphql(MUTATION_UPDATE_STORAGE, {
    props: props => ({
      onUpdateStorage: (id, storageUpdateInput) => {
        return props
          .mutate({
            variables: { id, storageUpdateInput },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  connect(
    mapState,
    mapDispatch
  ),
  reduxForm(formSettings)
)(StorageForm);



// WEBPACK FOOTER //
// ./src/containers/StorageForm/index.js